import { useCallback, useState } from "react";
import ExcelJS from "exceljs";
import { toast } from "react-toastify";
import apiClient from "../../../utils/apiClient";

function buildTimestamp() {
  return new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace("T", "_")
    .split(".")[0];
}

function toRenamedFile(file) {
  const originalName = file.name;
  const fileExtension = originalName.split(".").pop();
  const fileName = originalName.replace(`.${fileExtension}`, "");
  const newFileName = `${fileName}_${buildTimestamp()}.${fileExtension}`;
  return new File([file], newFileName, { type: file.type });
}

async function readSheetList(file, fileExtension) {
  if (fileExtension !== "xlsx") {
    return [];
  }

  try {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const sheetList = [];

    workbook.eachSheet((worksheet, sheetId) => {
      sheetList.push({ information: worksheet, id: sheetId });
    });

    return sheetList;
  } catch (error) {
    console.error("Error reading Excel file:", error);
    return [];
  }
}

export function useRateCardConverter() {
  const [files, setFiles] = useState([]);
  const [metadataMap, setMetadataMap] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    setIsSubmitting(true);

    const newFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        const fileExtension = file.name.split(".").pop();
        const sheetList = await readSheetList(file, fileExtension);

        return {
          originalFile: file,
          renamedFile: toRenamedFile(file),
          sheetList,
        };
      })
    );

    setFiles((prev) => [...prev, ...newFiles]);
    setIsSubmitting(false);
  }, []);

  const handleMetadataUpdate = useCallback((filename, metadata) => {
    setMetadataMap((prev) => ({
      ...prev,
      [filename]: metadata,
    }));
  }, []);

  const removeFile = useCallback((name) => {
    setFiles((prev) => prev.filter((file) => file.renamedFile.name !== name));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setMetadataMap({});
  }, []);

  const handleSubmit = useCallback(async () => {
    if (files.length === 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file.renamedFile));

      const metadataList = files.flatMap(
        (file) =>
          metadataMap[file.renamedFile.name]?.map((metadata) => ({
            file_name: file.renamedFile.name,
            ...metadata,
          })) || []
      );

      formData.append("metadata", JSON.stringify(metadataList));

      const response = await apiClient.post("/api/v1/rateCardConverter/upload", formData);

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          } else if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }

        toast.error(errorMessage);
        return;
      }

      const contentType = response.headers.get("Content-Type");

      if (
        response.ok &&
        contentType &&
        contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
      ) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Pricing_Template_${buildTimestamp()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }

      const errorText = await response.text();
      console.error("Download failed. Response not Excel:", errorText);
      toast.error("Upload succeeded, but the server returned an unexpected format.");
    } catch (err) {
      if (!err.message.includes("Upload failed.")) {
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [files, metadataMap]);

  return {
    files,
    isSubmitting,
    onDrop,
    handleSubmit,
    handleMetadataUpdate,
    removeFile,
    clearFiles,
  };
}
